<template>
  <ValidationObserver ref="fleetUploadForm">
    <div class="card card-custom">
      <div class="card-body container">

        <b-alert v-if="errorAlert" variant="warning" show>
          <div class="text-dark" v-html="errorAlert"></div>
        </b-alert>

        <b-alert v-if="formData && !isFormEditable" variant="secondary" show>
          <div class="text-dark">This form is already submitted and cannot be modified.</div>
        </b-alert>

        <h4 class="font-weight-bold" v-if="formData">
          Fleet Upload Effective Period :
          <b-badge variant="primary" class="p-2">
            {{ formData.effectiveStartDate | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
            to
            {{ formData.effectiveEndDate | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
          </b-badge>
        </h4>

        <div v-if="!isFormEditable && uploadedFleetLink" class="d-flex justify-content-start mt-5">
          <a :href="uploadedFleetLink" target="_blank" class="btn btn-primary">View Uploaded Fleet Declaration</a>
        </div>

        <b-card v-if="isFormEditable" class="mb-5 mt-3" body-class="px-7 py-5" >
          <div>
            <div>
              <p class="font-weight-bolder">Please upload a list of your vehicles (Excel format is recommended).
                Provide as much information as possible, including -</p>
              <ul class="ml-10">
                <li>Vehicle Registration Number and Registration State (if registered vehicle)<span class="text-danger">*</span></li>
                <li>VIN / Serial Number (if unregistered vehicle or plant/machinery)<span class="text-danger">*</span></li>
                <li>Make</li>
                <li>Model</li>
                <li>Year</li>
                <li>Market&nbsp;Value</li>
                <li>Location Postcode<span class="text-danger">*</span></li>
              </ul>
              <p><b>Note</b> - Either the Vehicle Registration Number <b>or</b> VIN/Serial Number <b>and</b> state <b>and</b> location postcode for the vehicles are mandatory.</p>

              <div class="form-group mb-2">
                <a class="btn btn-borderless text-dark" href="https://dkg.box.com/shared/static/6nondet0fmjluhi787ncvrs9eve45418.xlsx">
                  <i class="fa fa-sm fa-download"></i>Download Sample&nbsp;Template
                </a>
              </div>
            </div>

            <files-upload upload-key="fleetFile" :upload-in-progress="uploadInProgress" accept=".xlsx,.csv,.pdf" @uploadFiles="uploadFiles"/>

          </div>
        </b-card>

      </div>
    </div>
  </ValidationObserver>
</template>

<style lang="scss">

.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step-disabled {
  cursor: not-allowed !important;
}

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import _ from "lodash";
import Swal from "sweetalert2";
import KTUtil from "@/assets/js/components/util";
import stringify from "fast-json-stable-stringify";
import BrandService from "@/core/services/brand.service";
import FormData from "form-data";
import FilesUpload from "@/view/pages/wizard/motor-proposal-form/components/FilesUpload.vue";
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import ApiService from "@/core/services/api.service";

export default {
  name: "FleetUploadForm",

  components: {FilesUpload},

  data() {
    return {
      errorAlert: null,
      formId: null,
      formData: null,
      formDataString: null,
      wizardObj: null,
      uploadInProgress: false
    };
  },

  mounted() {
    this.startPageLoading();

    // Extract information from URL params
    const queryParams = this.$route.query;
    if (!queryParams.formId) {
      this.handleError("Unauthorised access!");
      return;
    }

    // Extract data from URL params
    this.formId = queryParams.formId;

    // Get form data.
    this.getFormData();
  },

  computed: {

    isFormEditable: function () {
      return this.formData && (this.formData.formStatus === 'Initialised' || this.formData.formStatus === 'Incomplete');
    },

    uploadedFleetLink: function () {
      if(this.formData && this.formData.formFleetFile) {
        return this.formData.formFleetFile.viewLink;
      }
      return null;
    }
  },

  methods: {

    getFormData: function () {
      ApiService.get(`/fleet-upload/form/${this.formId}`)
          .then(({ data }) => {
            this.formData = data.result;
            this.formDataString = stringify(this.formData);

            // Set UI Brand
            BrandService.setUIBrand(this.formData.brand);

            // Set wizard title and subtitle
            this.$store.commit("setWizardTitle", `Fleet Upload : ${this.formData.policyNumber}`);
            this.$store.commit("setWizardSubtitle", this.formData.clientName);

            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    saveFormData: function () {
      if (!this.isFormEditable) {
        return;
      }

      // Save form-data to the server - only if it has changed
      let newFormDataString = stringify(this.formData);
      if (newFormDataString !== this.formDataString) {
        // Show page loading
        this.startPageLoading();

        ApiService.post(`/fleet-upload/form/${this.formId}`, this.formData)
          .then(() => {
            this.formDataString = newFormDataString;

            // Stop page loading
            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
      }
    },

    uploadFiles: function (uploadKey, selectedFiles) {
      const formData = new FormData();
      for (const [i, file] of selectedFiles.entries()) {
        formData.append(`uploadedFiles[${i}]`, file);
      }

      const config = {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      };

      // Upload indicator
      this.uploadInProgress = true;

      ApiService.post(`/mpf/form/${this.formId}/uploads/${uploadKey}`, formData, config)
          .then(({ data }) => {
            this.uploadInProgress = false;

            let uploadedFiles = data.result;
            if(Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
              // Get the latest uploaded file
              uploadedFiles = _.orderBy(uploadedFiles, ['createdTime'], ['desc']);
              this.formData.formFleetFile = uploadedFiles[0];
            }
            else {
              this.handleError(`Error uploading files. Please try again.`);
              return;
            }
            this.fleetUploadCompleted();
          })
          .catch((error) => {
            this.handleError(error);
            this.uploadInProgress = false;
          });
    },

    fleetUploadCompleted: function () {
      this.startPageLoading();
      ApiService.post(`/fleet-upload/form/${this.formId}/submit`, this.formData)
          .then(() => {
            this.stopPageLoading();
            this.formData.formStatus = 'Active';

            Swal.fire({
              titleText: "Fleet Upload Successful",
              html: `<p>Thank you for uploading your fleet details.</p><br/>
                     <span class="font-weight-bold">Please close this browser window.</span>`,
              icon: "success",
              width: "40rem",
              allowOutsideClick: false,
              allowEnterKey: false,
              allowEscapeKey: false,
              showConfirmButton: false,
              showCancelButton: false,
              showCloseButton: false
            });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }
      if (!errorMsg) {
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },
};
</script>
